/**
 * Firebase Connection Test Utility
 * Comprehensive testing for Firebase connectivity and authentication issues
 */

import { auth, db } from '@/config/firebase';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';

export interface ConnectionTestResult {
  test: string;
  success: boolean;
  error?: string;
  details?: any;
  duration?: number;
}

export interface FirebaseHealthCheck {
  overall: 'healthy' | 'degraded' | 'unhealthy';
  tests: ConnectionTestResult[];
  summary: {
    passed: number;
    failed: number;
    total: number;
  };
  recommendations: string[];
}

/**
 * Test Firebase Auth service connectivity
 */
export async function testAuthConnectivity(): Promise<ConnectionTestResult> {
  const startTime = Date.now();
  
  try {
    // Test if auth service is accessible
    const currentUser = auth.currentUser;
    const isConnected = auth.app !== undefined;
    
    return {
      test: 'Auth Service Connectivity',
      success: isConnected,
      details: {
        currentUser: currentUser ? currentUser.uid : null,
        authDomain: auth.config.authDomain,
        projectId: auth.config.projectId
      },
      duration: Date.now() - startTime
    };
  } catch (error) {
    return {
      test: 'Auth Service Connectivity',
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      duration: Date.now() - startTime
    };
  }
}

/**
 * Test Firestore connectivity
 */
export async function testFirestoreConnectivity(): Promise<ConnectionTestResult> {
  const startTime = Date.now();

  try {
    // Test Firestore connectivity by checking if the database instance is available
    // This doesn't require reading any documents or authentication
    const isConnected = db && db.app && db.app.options.projectId;

    if (!isConnected) {
      throw new Error('Firestore database instance not available');
    }

    return {
      test: 'Firestore Connectivity',
      success: true,
      details: {
        projectId: db.app.options.projectId,
        databaseId: db._delegate?._databaseId?.database || 'default',
        connected: true
      },
      duration: Date.now() - startTime
    };
  } catch (error) {
    return {
      test: 'Firestore Connectivity',
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      duration: Date.now() - startTime
    };
  }
}

/**
 * Test network connectivity to Firebase services
 */
export async function testNetworkConnectivity(): Promise<ConnectionTestResult> {
  const startTime = Date.now();

  try {
    const authDomain = auth.config.authDomain;
    const apiKey = auth.config.apiKey;

    let authDomainReachable = false;
    let firebaseApiReachable = false;
    let authDomainStatus = 'unknown';
    let apiStatus = 'unknown';
    let networkError = '';

    // Test auth domain connectivity with better error handling
    try {
      // Use GET instead of HEAD as some servers handle HEAD requests differently
      const authResponse = await fetch(`https://${authDomain}/__/auth/handler`, {
        method: 'GET',
        mode: 'no-cors',
        cache: 'no-cache'
      });
      authDomainReachable = true;
      authDomainStatus = authResponse.status?.toString() || 'no-cors-success';
    } catch (authError) {
      authDomainReachable = false;
      authDomainStatus = 'failed';
      networkError += `Auth domain error: ${authError instanceof Error ? authError.message : 'Unknown'}; `;
    }

    // Test Firebase API connectivity with better error handling
    try {
      const apiResponse = await fetch(`https://identitytoolkit.googleapis.com/v1/accounts:lookup?key=${apiKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken: 'test' }),
        cache: 'no-cache'
      });
      // 400 is expected for invalid test data - it means API is reachable and responding
      firebaseApiReachable = apiResponse.status >= 200 && apiResponse.status < 500;
      apiStatus = apiResponse.status?.toString() || 'unknown';
    } catch (apiError) {
      firebaseApiReachable = false;
      apiStatus = 'failed';
      networkError += `API error: ${apiError instanceof Error ? apiError.message : 'Unknown'}`;
    }

    // Consider test successful if either service is reachable
    const success = authDomainReachable || firebaseApiReachable;

    return {
      test: 'Network Connectivity',
      success,
      error: success ? undefined : networkError.trim(),
      details: {
        authDomainReachable,
        firebaseApiReachable,
        authDomainStatus,
        apiStatus,
        authDomain,
        note: 'CORS restrictions may prevent accurate auth domain testing'
      },
      duration: Date.now() - startTime
    };
  } catch (error) {
    return {
      test: 'Network Connectivity',
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      duration: Date.now() - startTime
    };
  }
}

/**
 * Test Firebase configuration validity
 */
export async function testConfigurationValidity(): Promise<ConnectionTestResult> {
  const startTime = Date.now();
  
  try {
    const config = auth.config;
    const issues = [];
    
    // Check required fields
    if (!config.apiKey || config.apiKey === 'your_api_key_here') {
      issues.push('Invalid or missing API key');
    }
    
    if (!config.authDomain) {
      issues.push('Missing auth domain');
    }
    
    if (!config.projectId) {
      issues.push('Missing project ID');
    }
    
    // Validate API key format
    if (config.apiKey && !config.apiKey.startsWith('AIza')) {
      issues.push('API key format appears invalid');
    }
    
    // Validate auth domain format
    if (config.authDomain && !config.authDomain.includes('.firebaseapp.com')) {
      issues.push('Auth domain format appears invalid');
    }
    
    return {
      test: 'Configuration Validity',
      success: issues.length === 0,
      error: issues.length > 0 ? issues.join(', ') : undefined,
      details: {
        apiKey: config.apiKey ? `${config.apiKey.substring(0, 10)}...` : 'missing',
        authDomain: config.authDomain,
        projectId: config.projectId,
        issues
      },
      duration: Date.now() - startTime
    };
  } catch (error) {
    return {
      test: 'Configuration Validity',
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      duration: Date.now() - startTime
    };
  }
}

/**
 * Test authentication flow with test credentials
 */
export async function testAuthenticationFlow(email?: string, password?: string): Promise<ConnectionTestResult> {
  const startTime = Date.now();
  
  if (!email || !password) {
    return {
      test: 'Authentication Flow',
      success: false,
      error: 'Test credentials not provided',
      duration: Date.now() - startTime
    };
  }
  
  try {
    // Try to sign in with test credentials
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    
    return {
      test: 'Authentication Flow',
      success: true,
      details: {
        uid: userCredential.user.uid,
        email: userCredential.user.email,
        emailVerified: userCredential.user.emailVerified
      },
      duration: Date.now() - startTime
    };
  } catch (error: any) {
    return {
      test: 'Authentication Flow',
      success: false,
      error: error.message || 'Authentication failed',
      details: {
        errorCode: error.code,
        errorMessage: error.message
      },
      duration: Date.now() - startTime
    };
  }
}

/**
 * Run comprehensive Firebase health check
 */
export async function runFirebaseHealthCheck(testCredentials?: { email: string; password: string }): Promise<FirebaseHealthCheck> {
  const tests: ConnectionTestResult[] = [];
  
  // Run all tests
  tests.push(await testConfigurationValidity());
  tests.push(await testNetworkConnectivity());
  tests.push(await testAuthConnectivity());
  tests.push(await testFirestoreConnectivity());
  
  if (testCredentials) {
    tests.push(await testAuthenticationFlow(testCredentials.email, testCredentials.password));
  }
  
  // Calculate summary
  const passed = tests.filter(t => t.success).length;
  const failed = tests.filter(t => !t.success).length;
  const total = tests.length;
  
  // Determine overall health
  let overall: 'healthy' | 'degraded' | 'unhealthy';
  if (failed === 0) {
    overall = 'healthy';
  } else if (failed <= total / 2) {
    overall = 'degraded';
  } else {
    overall = 'unhealthy';
  }
  
  // Generate recommendations
  const recommendations: string[] = [];
  
  const configTest = tests.find(t => t.test === 'Configuration Validity');
  if (configTest && !configTest.success) {
    recommendations.push('Check Firebase configuration in environment variables');
  }
  
  const networkTest = tests.find(t => t.test === 'Network Connectivity');
  if (networkTest && !networkTest.success) {
    recommendations.push('Check internet connectivity and firewall settings');
    recommendations.push('Verify Firebase project is active and billing is enabled');
  }
  
  const authTest = tests.find(t => t.test === 'Auth Service Connectivity');
  if (authTest && !authTest.success) {
    recommendations.push('Check Firebase Authentication is enabled in console');
  }
  
  const firestoreTest = tests.find(t => t.test === 'Firestore Connectivity');
  if (firestoreTest && !firestoreTest.success) {
    recommendations.push('Check Firestore database is created and accessible');
  }
  
  if (recommendations.length === 0) {
    recommendations.push('All systems appear to be functioning normally');
  }
  
  return {
    overall,
    tests,
    summary: { passed, failed, total },
    recommendations
  };
}

/**
 * Fix common Firebase issues automatically
 */
export async function attemptAutoFix(): Promise<{ fixed: string[]; failed: string[] }> {
  const fixed: string[] = [];
  const failed: string[] = [];
  
  try {
    // Try to reinitialize Firebase services
    const healthCheck = await runFirebaseHealthCheck();
    
    if (healthCheck.overall !== 'healthy') {
      // Attempt to clear any cached auth state
      try {
        await auth.signOut();
        fixed.push('Cleared cached authentication state');
      } catch (error) {
        failed.push('Failed to clear auth state');
      }
      
      // Attempt to refresh the page if in browser
      if (typeof window !== 'undefined') {
        fixed.push('Page refresh recommended');
      }
    }
  } catch (error) {
    failed.push('Auto-fix failed: ' + (error instanceof Error ? error.message : 'Unknown error'));
  }
  
  return { fixed, failed };
}
